import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import { usePermissionCheck } from '../../../core/hooks/usePermissionCheck';
import {
	Card,
	CardContent,
	Typography,
	FormControlLabel,
	Checkbox,
	Grid,
	Divider,
	Button,
	CircularProgress,
} from '@mui/material';
import {
	notificationService,
	NotificationListItem,
	UserNotificationSetting,
} from '../../../core/services/notificationService';
import { useAuth } from '../../auth/context/AuthContext';

interface NotificationSettings {
	email: boolean;
	sms: boolean;
	push: boolean;
	disableEmailSelect?: boolean;
	notificationId?: number;
	id?: number; // ID from user_notification_settings
}

interface ProcessedNotification {
	title: string;
	categories: {
		title: string;
		notifications: Array<NotificationSettings & { title: string }>;
	}[];
}

//

const transformNotifications = (
	notifications: NotificationListItem[],
	userSettings: UserNotificationSetting[],
	isManager: boolean = false
): ProcessedNotification[] => {
	// Create a map of notification settings by notification URI for quick lookup
	const settingsMap = new Map(
		userSettings.map((setting) => {
			return [setting.notifications?.['@id'], setting];
		})
	);

	// Group notifications by their group and type
	const groupedNotifications = notifications.reduce(
		(acc, notification) => {
			const groupName = notification.notificationGroup.label;
			const typeName = notification.notificationType.label;
			const notificationUri = `/api/notification_lists/${notification.id}`;
			const userSetting = settingsMap.get(notificationUri);

			// Filter out Manager Notifications category if user is not a Manager
			if (!isManager && typeName === 'Manager Notifications') {
				return acc;
			}

			if (!acc[groupName]) {
				acc[groupName] = {
					title: groupName,
					categories: {},
				};
			}

			if (!acc[groupName].categories[typeName]) {
				acc[groupName].categories[typeName] = [];
			}

			// Combine notification info with user settings
			acc[groupName].categories[typeName].push({
				title: notification.label,
				email: userSetting?.email ?? false,
				sms: userSetting?.sms ?? false,
				push: userSetting?.push ?? false,
				notificationId: notification.id,
				...(userSetting && { id: userSetting.id }), // Include setting ID if it exists
				...((notification.label.toLowerCase().includes('welcome email') ||
					notification.label.toLowerCase().includes('email updated') ||
					notification.label.toLowerCase().includes('password updated')) && {
					disableEmailSelect: true,
					email: true,
				}),
			});

			return acc;
		},
		{} as Record<
			string,
			{
				title: string;
				categories: Record<string, Array<NotificationSettings & { title: string }>>;
			}
		>
	);

	// Convert the grouped object into the final array structure
	return Object.values(groupedNotifications).map((group) => ({
		title: group.title,
		categories: Object.entries(group.categories).map(([title, notifications]) => ({
			title,
			notifications,
		})),
	}));
};

export const NotificationSetting = () => {
	const { user } = useAuth();
	const { hasEditPermission } = usePermissionCheck();
	const canEditNotifications = hasEditPermission('Notifications');
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);

	// Check if the user is a Manager based on their security role type
	const isManager = user?.securityRole?.securityRoleType?.label === 'Manager';
	console.log('Is Manager:', isManager, user?.securityRole?.securityRoleType);

	const formik = useFormik({
		initialValues: {
			notifications: [] as ProcessedNotification[],
		},
		onSubmit: async (values) => {
			try {
				setSaving(true);
				setError(null);

				const appData = JSON.parse(localStorage.getItem('BYOD-app'));
				const workplaceId = appData?.workplace;
				const userId = appData?.user?.id;

				if (!workplaceId || !userId) {
					throw new Error('Workplace or User ID not found');
				}

				// Flatten the notifications structure to get all notification settings
				const allNotifications = values.notifications.flatMap((module) =>
					module.categories.flatMap((category) =>
						category.notifications.map((notification) => ({
							id: notification.id,
							notificationId: notification.notificationId,
							email: notification.email,
							sms: notification.sms,
							push: notification.push,
						}))
					)
				);

				// Process each notification setting
				await Promise.all(
					allNotifications.map(async (notification) => {
						if (notification.id) {
							// Update existing setting
							await notificationService.updateUserNotificationSetting({
								id: notification.id,
								email: notification.email,
								sms: notification.sms,
								push: notification.push,
							});
						} else if (notification.notificationId) {
							// Create new setting
							await notificationService.createUserNotificationSetting({
								workplace: `/api/workplaces/${workplaceId}`,
								users: `/api/users/${userId}`,
								notifications: `/api/notification_lists/${notification.notificationId}`,
								email: notification.email,
								sms: notification.sms,
								push: notification.push,
							});
						}
					})
				);

				// Refresh data after updates
				const [notificationData, userSettingsData] = await Promise.all([
					notificationService.getNotificationLists(),
					notificationService.getUserNotificationSettings(),
				]);

				const transformedData = transformNotifications(notificationData, userSettingsData, isManager);
				formik.resetForm({ values: { notifications: transformedData } });
			} catch (err) {
				console.error('Error saving notifications:', err);
				setError('Failed to save notifications. Please try again.');
			} finally {
				setSaving(false);
			}
		},
	});

	console.log('Formik Values:', formik.values, formik.dirty);

	useEffect(() => {
		const loadData = async () => {
			try {
				setLoading(true);
				setError(null);

				// Fetch both notifications and user settings in parallel
				const [notificationData, userSettingsData] = await Promise.all([
					notificationService.getNotificationLists(),
					notificationService.getUserNotificationSettings(),
				]);

				// Transform and combine the data
				const transformedData = transformNotifications(notificationData, userSettingsData, isManager);
				formik.resetForm({ values: { notifications: transformedData } });
			} catch (err) {
				setError('Failed to load notifications. Please try again later.');
				console.error('Error loading data:', err);
			} finally {
				setLoading(false);
			}
		};

		loadData();
	}, []);

	const handleChange = (
		moduleIdx: number,
		catIdx: number,
		notifIdx: number,
		type: 'email' | 'sms' | 'push'
	) => {
		if (!canEditNotifications) return; // Prevent changes if user doesn't have edit permission
		
		const updatedNotifications = formik.values.notifications.map((module, mIdx) => ({
			...module,
			categories: module.categories.map((category, cIdx) => ({
				...category,
				notifications: category.notifications.map((notif, nIdx) =>
					mIdx === moduleIdx && cIdx === catIdx && nIdx === notifIdx
						? { ...notif, [type]: !notif[type] }
						: notif
				),
			})),
		}));
		formik.setFieldValue('notifications', updatedNotifications);
	};

	return (
		<Card sx={{ height: '100%', overflowY: 'scroll' }}>
			<CardContent>
				<Grid container justifyContent="space-between" alignItems="center">
					<Grid item>
						<Typography variant="h5" sx={{ fontFamily: 'Gotham' }}>Notifications - {user?.email}</Typography>
					</Grid>
					<Grid item>
						<Button
							variant="contained"
							sx={{ backgroundColor: '#446388' }}
							disabled={!formik.dirty || loading || saving || !canEditNotifications}
							onClick={() => formik.handleSubmit()}
						>
							{saving ? <CircularProgress size={20} /> : 'Save'}
						</Button>
					</Grid>
				</Grid>

				{loading ? (
					<Grid container justifyContent="center" sx={{ mt: 4 }}>
					<CircularProgress size={20} color="inherit" />
					</Grid>
				) : error ? (
					<Typography color="error" sx={{ mt: 4 }}>
						{error}
					</Typography>
				) : (
					<>
						{formik.values.notifications.map((module, moduleIdx) => (
							<div key={moduleIdx} style={{ marginBottom: 24, marginTop: 24 }}>
								<Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1, fontFamily: 'Gotham' }}>
									{module.title}
								</Typography>
								{module.categories.map((category, catIdx) => (
									<div key={catIdx} style={{ marginLeft: 16, marginBottom: 16 }}>
										<Typography variant="body2" fontWeight={600} sx={{ mb: 1, fontFamily: 'Gotham' }}>
											{category.title}
										</Typography>
										{category.notifications.length === 0 ? (
											<Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Gotham' }}>
												No notifications
											</Typography>
										) : (
											category.notifications.map((notif, notifIdx) => (
												<Grid
													container
													spacing={2}
													alignItems="center"
													key={notifIdx}
													sx={{ mb: 1 }}
												>
													<Grid item xs={12} sm={5}>
														<Typography variant="body2" sx={{ fontFamily: 'Gotham' }}>{notif.title}</Typography>
													</Grid>
													<Grid item xs={4} sm={2}>
														<FormControlLabel
															control={
																<Checkbox
																	checked={notif.email}
																	onChange={() => {
																		handleChange(moduleIdx, catIdx, notifIdx, 'email');
																	}}
																	disabled={notif.disableEmailSelect || !canEditNotifications}
																/>
															}
															label="Email"
														/>
													</Grid>
													{/* SMS option hidden
													<Grid item xs={4} sm={2}>
														<FormControlLabel
															control={
																<Checkbox
																	checked={notif.sms}
																	onChange={() => {
																		handleChange(moduleIdx, catIdx, notifIdx, 'sms');
																	}}
																	disabled={!canEditNotifications}
																/>
															}
															label="SMS"
														/>
													</Grid>
													*/}
													<Grid item xs={4} sm={2}>
														<FormControlLabel
															control={
																<Checkbox
																	checked={notif.push}
																	onChange={() => {
																		handleChange(moduleIdx, catIdx, notifIdx, 'push');
																	}}
																	disabled={!canEditNotifications}
																/>
															}
															label="Mabel™"
														/>
													</Grid>
												</Grid>
											))
										)}
										{catIdx < module.categories.length - 1 && <Divider sx={{ my: 1 }} />}
									</div>
								))}
								{moduleIdx < formik.values.notifications.length - 1 && <Divider sx={{ my: 2 }} />}
							</div>
						))}
					</>
				)}
			</CardContent>
		</Card>
	);
};
